import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  getSDEEvent,
  getEventRegistrations,
  getAllCandidatures,
  getEventMeetings,
  updateCandidatureStatus,
  deleteMeetingsForPair,
} from '../../Components/SDE24/sdeSupabaseService';
import { STATUS_COLORS, PROFIL_CREATEUR_OPTIONS, ADHERENT_OPTIONS, ATTENDANCE_MODES } from '../../Components/SDE24/sdeConstants';

// ─── Theme ─────────────────────────────────────────
const theme = {
  primary: '#2563EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  purple: '#8B5CF6',
  gray: { 50: '#F9FAFB', 100: '#F3F4F6', 200: '#E5E7EB', 300: '#D1D5DB', 400: '#9CA3AF', 500: '#6B7280', 600: '#4B5563', 700: '#374151', 800: '#1F2937' },
};

// ─── Styled ────────────────────────────────────────
const Page = styled.div` max-width: 1400px; margin: 0 auto; padding: 2rem; `;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem; flex-wrap: wrap; gap: 1rem;
  h1 { font-size: 1.5rem; font-weight: 700; color: ${theme.gray[800]}; margin: 0; }
`;

const BackBtn = styled.button`
  background: white; border: 1px solid ${theme.gray[300]}; padding: 0.5rem 1rem; border-radius: 8px; cursor: pointer; font-size: 0.875rem;
  &:hover { background: ${theme.gray[50]}; }
`;

const CompanySelector = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem;
`;

const CompanyChip = styled.button`
  padding: 0.5rem 1rem; border-radius: 999px; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  border: 2px solid ${p => p.active ? theme.primary : theme.gray[300]};
  background: ${p => p.active ? '#EFF6FF' : 'white'};
  color: ${p => p.active ? theme.primary : theme.gray[600]};
  transition: all 0.15s;
  &:hover { border-color: ${theme.primary}; }
`;

const CandidatureCard = styled.div`
  background: white; border: 1px solid ${theme.gray[200]}; border-radius: 12px; padding: 1.25rem; margin-bottom: 0.75rem;
  display: flex; align-items: center; gap: 1rem;
  border-left: 4px solid ${p =>
    p.status === 'confirmed' ? theme.success :
    p.status === 'rejected' ? theme.error :
    p.status === 'waitlisted' ? theme.purple :
    theme.warning};
  transition: all 0.15s;
  &:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
`;

const PriorityBadge = styled.div`
  width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.95rem; flex-shrink: 0;
  background: ${p => p.rank <= 3 ? '#D1FAE5' : p.rank <= 7 ? '#FEF3C7' : theme.gray[100]};
  color: ${p => p.rank <= 3 ? '#065F46' : p.rank <= 7 ? '#92400E' : theme.gray[600]};
`;

const CandInfo = styled.div`
  flex: 1; min-width: 0;
  .name { font-weight: 600; font-size: 1rem; color: ${theme.gray[800]}; }
  .sub { font-size: 0.85rem; color: ${theme.gray[500]}; margin-top: 0.15rem; }
`;

const Badge = styled.span`
  display: inline-block; padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600;
  background: ${p => p.bg || theme.gray[100]}; color: ${p => p.fg || theme.gray[700]};
`;

const ActionBtn = styled.button`
  padding: 0.35rem 0.75rem; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => p.color || theme.gray[300]};
  color: ${p => p.color || theme.gray[700]};
  background: white; transition: all 0.15s;
  &:hover { background: ${p => p.color ? p.color + '10' : theme.gray[50]}; }
  & + & { margin-left: 0.35rem; }
`;

const SummaryBar = styled.div`
  display: flex; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  padding: 0.5rem 1rem; background: white; border: 1px solid ${theme.gray[200]}; border-radius: 8px;
  font-size: 0.85rem; color: ${theme.gray[600]};
  span { font-weight: 700; color: ${p => p.accent || theme.gray[800]}; margin-right: 0.25rem; }
`;

const EmptyState = styled.div`
  text-align: center; padding: 3rem; color: ${theme.gray[400]};
  font-size: 0.95rem;
`;

const LoadingBox = styled.div`
  display: flex; align-items: center; justify-content: center; min-height: 60vh; font-size: 1.1rem; color: ${theme.gray[500]};
`;

const ViewToggle = styled.div`
  display: flex; gap: 0.5rem; align-items: center;
`;
const ToggleBtn = styled.button`
  padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => p.active ? theme.primary : theme.gray[300]};
  background: ${p => p.active ? '#EFF6FF' : 'white'};
  color: ${p => p.active ? theme.primary : theme.gray[600]};
  &:hover { border-color: ${theme.primary}; }
`;
const ExportBtn = styled.button`
  padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  border: 1px solid ${theme.gray[300]}; background: white; color: ${theme.gray[700]};
  &:hover { background: ${theme.gray[50]}; }
`;
const TableWrapper = styled.div` overflow-x: auto; margin-top: 1rem; `;
const STable = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.85rem;
  th, td { padding: 0.6rem 0.75rem; border-bottom: 1px solid ${theme.gray[200]}; text-align: left; vertical-align: top; }
  th { background: ${theme.gray[100]}; font-weight: 700; color: ${theme.gray[700]}; position: sticky; top: 0; cursor: pointer; user-select: none; white-space: nowrap; }
  th:hover { background: ${theme.gray[200]}; }
  tr:hover td { background: ${theme.gray[50]}; }
`;
const StatusSelectInline = styled.select`
  padding: 0.3rem 0.5rem; border-radius: 6px; font-size: 0.8rem; font-weight: 600; border: 1px solid ${theme.gray[300]}; cursor: pointer;
  background: ${p => p.value === 'confirmed' ? '#D1FAE5' : p.value === 'rejected' ? '#FEE2E2' : p.value === 'waitlisted' ? '#EDE9FE' : '#FEF3C7'};
  color: ${p => p.value === 'confirmed' ? '#065F46' : p.value === 'rejected' ? '#991B1B' : p.value === 'waitlisted' ? '#5B21B6' : '#92400E'};
`;
const FilterBar = styled.div`
  display: flex; gap: 0.75rem; align-items: center; margin: 1rem 0; flex-wrap: wrap;
  select, input { padding: 0.4rem 0.75rem; border: 1px solid ${theme.gray[300]}; border-radius: 6px; font-size: 0.85rem; }
  input { min-width: 200px; }
`;
const SavedCheck = styled.span` color: ${theme.success}; font-size: 0.75rem; font-weight: 600; margin-left: 0.35rem; `;
const MessageTd = styled.td` max-width: 250px; white-space: pre-wrap; word-break: break-word; color: ${theme.gray[500]}; font-size: 0.8rem; `;

// ─── Component ─────────────────────────────────────
const SDEShortlistAdmin = ({ fullUser }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventId = searchParams.get('eid');

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // Planned slot label for a candidature's designer/company pair ('' when not in the planning)
  const plannedSlot = useMemo(() => {
    const slots = event?.sde_config?.slots || [];
    const label = (id) => { const i = slots.findIndex(s => s.id === id); return i >= 0 ? (slots[i].label || `RDV ${i + 1}`).replace(/ - .*$/, '') : id; };
    const map = {};
    meetings.forEach(m => { map[`${m.designer_registration_id}|${m.company_registration_id}`] = label(m.slot_id); });
    return (c) => map[`${c.designer_registration_id}|${c.company_registration_id}`] || '';
  }, [meetings, event]);

  useEffect(() => {
    if (!eventId) return;
    loadData();
    // eslint-disable-next-line
  }, [eventId]);

  const loadData = async () => {
    setLoading(true);
    const [evRes, regsRes, candsRes, meetRes] = await Promise.all([
      getSDEEvent(eventId),
      getEventRegistrations(eventId, 'pop_a'),
      getAllCandidatures(eventId),
      getEventMeetings(eventId),
    ]);

    setEvent(evRes.data);
    const validCompanies = (regsRes.data || []).filter(r => r.status === 'submitted' || r.status === 'validated');
    setCompanies(validCompanies);
    setCandidatures(candsRes.data || []);
    setMeetings(meetRes.data || []);

    if (validCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(validCompanies[0].id);
    }
    setLoading(false);
  };

  const companyCands = candidatures
    .filter(c => c.company_registration_id === selectedCompanyId)
    .sort((a, b) => a.priority - b.priority);

  // Un-confirming a candidature removes its planned meeting so the planning never shows ghost cells
  const setCandidatureStatus = async (candidatureId, newStatus) => {
    await updateCandidatureStatus(candidatureId, newStatus);
    const cand = candidatures.find(c => c.id === candidatureId);
    if (cand && newStatus !== 'confirmed') {
      await deleteMeetingsForPair(eventId, cand.designer_registration_id, cand.company_registration_id);
    }
  };

  const handleStatusUpdate = async (candidatureId, newStatus) => {
    await setCandidatureStatus(candidatureId, newStatus);
    loadData();
  };

  const statusActions = (cand) => {
    const s = cand.status;
    const btns = [];
    if (s !== 'confirmed') btns.push({ label: 'Confirmer', status: 'confirmed', color: theme.success });
    if (s !== 'rejected') btns.push({ label: 'Refuser', status: 'rejected', color: theme.error });
    if (s !== 'waitlisted') btns.push({ label: 'Liste att.', status: 'waitlisted', color: theme.purple });
    if (s !== 'pending') btns.push({ label: 'Reset', status: 'pending', color: theme.warning });
    return btns;
  };

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  // ─── Table View State ─────────────────────────────
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [tableFilter, setTableFilter] = useState('all');
  const [tableSearch, setTableSearch] = useState('');
  const [sortCol, setSortCol] = useState('companyName');
  const [sortDir, setSortDir] = useState('asc');
  const [savedId, setSavedId] = useState(null);

  const tableRows = useMemo(() => {
    return candidatures.map(c => {
      const company = companies.find(co => co.id === c.company_registration_id);
      const d = c.designer;
      const rep = d?.representative_1 || {};
      return {
        id: c.id,
        companyName: company?.company_name || '',
        designerName: d?.company_name || d?.user_email || '',
        contact: `${rep.firstname || ''} ${rep.name || ''}`.trim(),
        email: rep.email || d?.user_email || '',
        phone: rep.phone || '',
        status: c.status,
        priority: c.priority ?? '',
        message: c.message || '',
        planning: plannedSlot(c) || (c.status === 'confirmed' ? 'À planifier' : ''),
      };
    });
  }, [candidatures, companies, plannedSlot]);

  const filteredTableRows = useMemo(() => {
    let rows = tableRows;
    if (tableFilter === 'to_plan') rows = rows.filter(r => r.planning === 'À planifier');
    else if (tableFilter !== 'all') rows = rows.filter(r => r.status === tableFilter);
    if (tableSearch) {
      const q = tableSearch.toLowerCase();
      rows = rows.filter(r => r.companyName.toLowerCase().includes(q) || r.designerName.toLowerCase().includes(q) || r.contact.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
    }
    return [...rows].sort((a, b) => {
      let va = a[sortCol] ?? '', vb = b[sortCol] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableRows, tableFilter, tableSearch, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortArrow = (col) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleTableStatusChange = async (candId, newStatus) => {
    await setCandidatureStatus(candId, newStatus);
    setSavedId(candId);
    setTimeout(() => setSavedId(null), 2000);
    loadData();
  };

  const tableCounts = useMemo(() => {
    const c = { confirmed: 0, pending: 0, rejected: 0, waitlisted: 0, to_plan: 0 };
    tableRows.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; if (r.planning === 'À planifier') c.to_plan++; });
    return c;
  }, [tableRows]);

  // Export = table columns + the full Pop B application (designer side of each candidature)
  const exportCSV = () => {
    const headers = [
      'Entreprise', 'Designer', 'Contact', 'Email', 'Téléphone', 'Statut', 'Planning', 'Priorité', 'Message',
      'Fonction', 'Téléphone fixe', 'Contact 2', 'Fonction contact 2', 'Email contact 2', 'Téléphone contact 2',
      'Site internet', 'Instagram', 'Profil créateur', 'Adhérent / cotisant', 'Mode de participation',
      'Savoir-faire', 'Matériaux et techniques', 'Démarche créative', 'Accord de confidentialité signé le',
      'Nb projets portfolio', 'Projets portfolio', 'Images portfolio',
    ];
    const statusLabels = { confirmed: 'Confirmé', rejected: 'Refusé', waitlisted: 'Liste attente', pending: 'En attente' };
    const optLabel = (opts, v) => { const o = opts.find(x => x.value === v); return o ? (o.label_fr || o.label) : v; };
    const candById = Object.fromEntries(candidatures.map(c => [c.id, c]));
    const rows = filteredTableRows.map(r => {
      const d = candById[r.id]?.designer || {};
      const meta = d.metadata || {};
      const rep1 = d.representative_1 || {};
      const rep2 = d.representative_2 || {};
      const projects = d.portfolio_projects || [];
      return [
        r.companyName, r.designerName, r.contact, r.email, r.phone,
        statusLabels[r.status] || r.status, r.planning, r.priority, r.message,
        rep1.job_title, rep1.landline,
        `${rep2.firstname || ''} ${rep2.name || ''}`.trim(), rep2.job_title, rep2.email, rep2.phone,
        meta.website, meta.instagram,
        (meta.profil_createur || []).map(v => optLabel(PROFIL_CREATEUR_OPTIONS, v)).join(', '),
        (meta.adherent_cotisant || []).map(v => optLabel(ADHERENT_OPTIONS, v)).join(', '),
        optLabel(ATTENDANCE_MODES, d.attendance_mode),
        meta.savoir_faire, meta.materiaux_techniques, meta.demarche_creative,
        meta.accord_signe_at ? new Date(meta.accord_signe_at).toLocaleDateString('fr-FR') : '',
        projects.length,
        projects.map(p => {
          const info = [p.type, p.year, [p.city, p.country].filter(Boolean).join(', '), p.confidentiality === 'confidentiel' ? 'confidentiel' : ''].filter(Boolean).join(' · ');
          return `${p.title || 'Sans titre'}${info ? ` (${info})` : ''}${p.description ? ` : ${p.description}` : ''}`;
        }).join(' | '),
        projects.flatMap(p => (p.files || []).map(f => f.publicUrl)).filter(Boolean).join(' '),
      ].map(v => `"${(v ?? '').toString().replace(/\s*\n\s*/g, ' ').replace(/"/g, '""')}"`);
    });
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `shortlists_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingBox>Chargement des shortlists...</LoadingBox>;

  const confirmedCount = companyCands.filter(c => c.status === 'confirmed').length;
  const pendingCount = companyCands.filter(c => c.status === 'pending').length;
  const rejectedCount = companyCands.filter(c => c.status === 'rejected').length;
  const waitlistedCount = companyCands.filter(c => c.status === 'waitlisted').length;
  const toPlanCount = companyCands.filter(c => c.status === 'confirmed' && !plannedSlot(c)).length;

  return (
    <Page>
      <Header>
        <div>
          <BackBtn onClick={() => navigate(`/admin/events/sde?eid=${eventId}`)}>← Retour au dashboard</BackBtn>
          <h1 style={{ marginTop: '0.75rem' }}>{event?.title_fr || 'SDE'} — Shortlists</h1>
        </div>
        <ViewToggle>
          <ToggleBtn active={viewMode === 'cards'} onClick={() => setViewMode('cards')}>Par entreprise</ToggleBtn>
          <ToggleBtn active={viewMode === 'table'} onClick={() => setViewMode('table')}>Vue tableau</ToggleBtn>
          <ExportBtn onClick={exportCSV}>Export CSV</ExportBtn>
        </ViewToggle>
      </Header>

      {viewMode === 'table' ? (
        <>
          <FilterBar>
            <input type="text" placeholder="Rechercher entreprise, designer, email..." value={tableSearch} onChange={e => setTableSearch(e.target.value)} />
            <select value={tableFilter} onChange={e => setTableFilter(e.target.value)}>
              <option value="all">Tous ({tableRows.length})</option>
              <option value="confirmed">Confirmés ({tableCounts.confirmed})</option>
              <option value="to_plan">Confirmés à planifier ({tableCounts.to_plan})</option>
              <option value="pending">En attente ({tableCounts.pending})</option>
              <option value="waitlisted">Liste attente ({tableCounts.waitlisted})</option>
              <option value="rejected">Refusés ({tableCounts.rejected})</option>
            </select>
            <span style={{ fontSize: '0.85rem', color: theme.gray[500] }}>{filteredTableRows.length} résultat(s)</span>
          </FilterBar>
          <TableWrapper>
            <STable>
              <thead>
                <tr>
                  <th onClick={() => handleSort('companyName')}>Entreprise{sortArrow('companyName')}</th>
                  <th onClick={() => handleSort('designerName')}>Designer{sortArrow('designerName')}</th>
                  <th onClick={() => handleSort('contact')}>Contact{sortArrow('contact')}</th>
                  <th onClick={() => handleSort('email')}>Email{sortArrow('email')}</th>
                  <th onClick={() => handleSort('phone')}>Tél.{sortArrow('phone')}</th>
                  <th onClick={() => handleSort('status')}>Statut{sortArrow('status')}</th>
                  <th onClick={() => handleSort('planning')}>Planning{sortArrow('planning')}</th>
                  <th onClick={() => handleSort('priority')}>Prio.{sortArrow('priority')}</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {filteredTableRows.map(row => (
                  <tr key={row.id}>
                    <td><strong>{row.companyName}</strong></td>
                    <td>{row.designerName}</td>
                    <td>{row.contact}</td>
                    <td><a href={`mailto:${row.email}`}>{row.email}</a></td>
                    <td>{row.phone}</td>
                    <td>
                      <StatusSelectInline value={row.status} onChange={e => handleTableStatusChange(row.id, e.target.value)}>
                        <option value="pending">En attente</option>
                        <option value="confirmed">Confirmé</option>
                        <option value="waitlisted">Liste att.</option>
                        <option value="rejected">Refusé</option>
                      </StatusSelectInline>
                      {savedId === row.id && <SavedCheck>✓</SavedCheck>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600, color: row.planning === 'À planifier' ? theme.warning : theme.gray[700] }}>{row.planning}</td>
                    <td>{row.priority}</td>
                    <MessageTd>{row.message}</MessageTd>
                  </tr>
                ))}
                {filteredTableRows.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: theme.gray[400], padding: '2rem' }}>Aucun résultat</td></tr>
                )}
              </tbody>
            </STable>
          </TableWrapper>
        </>
      ) : (
      <>
      {/* Company Selector */}
      <CompanySelector>
        {companies.map(c => {
          const count = candidatures.filter(ca => ca.company_registration_id === c.id).length;
          return (
            <CompanyChip
              key={c.id}
              active={c.id === selectedCompanyId}
              onClick={() => setSelectedCompanyId(c.id)}
            >
              {c.company_name || c.user_email} ({count})
            </CompanyChip>
          );
        })}
      </CompanySelector>

      {selectedCompany && (
        <>
          <SummaryBar>
            <SummaryItem accent={theme.success}><span>{confirmedCount}</span> confirmées</SummaryItem>
            <SummaryItem accent={theme.primary}><span>{companyCands.filter(c => plannedSlot(c)).length}</span> au planning</SummaryItem>
            {toPlanCount > 0 && <SummaryItem accent={theme.warning}><span>{toPlanCount}</span> confirmées à planifier</SummaryItem>}
            <SummaryItem accent={theme.warning}><span>{pendingCount}</span> en attente</SummaryItem>
            <SummaryItem accent={theme.error}><span>{rejectedCount}</span> refusées</SummaryItem>
            <SummaryItem accent={theme.purple}><span>{waitlistedCount}</span> liste d'attente</SummaryItem>
          </SummaryBar>

          {companyCands.length === 0 ? (
            <EmptyState>Aucune candidature pour cette entreprise</EmptyState>
          ) : (
            companyCands.map(cand => {
              const designer = cand.designer;
              const si = STATUS_COLORS[cand.status] || { bg: '#f0f0f0', text: '#666', label_fr: cand.status };
              return (
                <CandidatureCard key={cand.id} status={cand.status}>
                  <PriorityBadge rank={cand.priority}>{cand.priority}</PriorityBadge>
                  <CandInfo>
                    <div className="name">
                      {designer?.company_name || designer?.user_email || 'Designer'}
                    </div>
                    <div className="sub">
                      {designer?.representative_1
                        ? `${designer.representative_1.firstname || ''} ${designer.representative_1.name || ''} · ${designer.representative_1.email || ''}`
                        : designer?.user_email || ''}
                    </div>
                  </CandInfo>
                  <Badge bg={si.bg} fg={si.text}>{si.label_fr}</Badge>
                  {plannedSlot(cand)
                    ? <Badge bg="#EFF6FF" fg="#1E40AF">{plannedSlot(cand)}</Badge>
                    : cand.status === 'confirmed' && <Badge bg="#FEF3C7" fg="#92400E">À planifier</Badge>}
                  <div>
                    {statusActions(cand).map(a => (
                      <ActionBtn key={a.status} color={a.color} onClick={() => handleStatusUpdate(cand.id, a.status)}>
                        {a.label}
                      </ActionBtn>
                    ))}
                  </div>
                </CandidatureCard>
              );
            })
          )}
        </>
      )}
      </>
      )}
    </Page>
  );
};

export default SDEShortlistAdmin;
